import { useContext, useEffect, useState } from 'react';
import _ from 'lodash';

import { FormStep, Matchup, Pick } from 'src/models';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from 'src/services/firebase-service';
import logging from 'src/services/logging';

import { Backdrop, CircularProgress } from '@mui/material';

import useUser from 'src/hooks/useUser';
import { CycleContext } from 'src/context/CycleContext';
import NewPicksCard from './NewPicksCard';

export const NewPicksPage = () => {
  const cycle = useContext(CycleContext);
  const [fetchingMatchups, setFetchingMatchups] = useState<boolean>(false);
  const [fetchingPicks, setFetchingPicks] = useState<boolean>(true);
  const [firstRoundFormSteps, setFirstRoundFormSteps] = useState<FormStep[]>(
    []
  );
  const [secondRoundFormSteps, setSecondRoundFormSteps] = useState<FormStep[]>(
    []
  );
  const [thirdRoundFormSteps, setThirdRoundFormSteps] = useState<FormStep[]>(
    []
  );
  const [fourthRoundFormSteps, setFourthRoundFormSteps] = useState<FormStep[]>(
    []
  );
  const [firstRoundPicks, setFirstRoundPicks] = useState<Pick[]>([]);
  const [secondRoundPicks, setSecondRoundPicks] = useState<Pick[]>([]);
  const [thirdRoundPicks, setThirdRoundPicks] = useState<Pick[]>([]);
  const [fourthRoundPicks, setFourthRoundPicks] = useState<Pick[]>([]);
  const { dbUser, fetchingUser } = useUser();

  useEffect(() => {
    const fetchPicks = async () => {
      try {
        if (dbUser) {
          logging.info('Fetching picks...', 'PicksPage');
          const q = query(
            collection(db, 'picks'),
            where('uid', '==', dbUser?.uid),
            where('season', '==', cycle.season)
          );
          const snapshot = await getDocs(q);
          const picks: Pick[] = [];
          snapshot.forEach((pick) => {
            picks.push(pick.data() as Pick);
          });
          const sortedPicks = _.orderBy(
            picks,
            ['playoffSeriesLetter'],
            ['asc']
          );
          setFirstRoundPicks(sortedPicks.filter((p) => p.playoffRound === 1));
          setSecondRoundPicks(sortedPicks.filter((p) => p.playoffRound === 2));
          setThirdRoundPicks(sortedPicks.filter((p) => p.playoffRound === 3));
          setFourthRoundPicks(sortedPicks.filter((p) => p.playoffRound === 4));
          setFetchingPicks(false);
        }
      } catch (error) {
        logging.error(error, 'PicksPage');
        setFetchingPicks(false);
      }
    };
    fetchPicks();
  }, [dbUser, cycle.season]);

  const buildFormSteps = (matchups: Matchup[]) => {
    if (matchups.length > 0) {
      logging.info('Building form steps...', 'PicksPage');
      const steps = matchups.map((match) => {
        const gamePk = match.gamePk;
        const playoffRound = match.playoffRound;
        const playoffSeriesLetter = match.playoffSeriesLetter;
        const season = match.season;
        const awayTeam = match.teams.away;
        const homeTeam = match.teams.home;
        const startTime = match.startTime;
        const awayTeamLeaders = awayTeam.leaders;
        const filteredAwayPlayers = awayTeam.roster.filter(
          (player) => !awayTeamLeaders.some((leader) => leader.id === player.id)
        );
        const completeAwayPlayers = [
          ...awayTeamLeaders,
          ...filteredAwayPlayers,
        ];
        const homeTeamLeaders = homeTeam.leaders;
        const filteredHomePlayers = homeTeam.roster.filter(
          (player) => !homeTeamLeaders.some((leader) => leader.id === player.id)
        );
        const completeHomePlayers = [
          ...homeTeamLeaders,
          ...filteredHomePlayers,
        ];

        return {
          gamePk,
          playoffRound,
          playoffSeriesLetter,
          season,
          homeTeam,
          homePlayers: completeHomePlayers,
          awayTeam,
          awayPlayers: completeAwayPlayers,
          startTime,
        } as FormStep;
      });
      return steps;
    } else {
      return [];
    }
  };

  useEffect(() => {
    const fetchMatchups = async () => {
      if (!fetchingPicks) {
        try {
          logging.info('Fetching matchups...', 'PicksPage');
          setFetchingMatchups(true);
          const q = query(
            collection(db, 'matchups'),
            where('season', '==', cycle.season)
          );
          const snapshot = await getDocs(q);
          const matchups: Matchup[] = [];
          snapshot.forEach((match) => {
            matchups.push(match.data() as Matchup);
          });

          const sortedMatchups = _.orderBy(
            matchups,
            ['playoffSeriesLetter'],
            ['asc']
          );
          logging.info({ sortedMatchups }, 'sortedMatchups');

          setFirstRoundFormSteps(
            buildFormSteps(sortedMatchups.filter((m) => m.playoffRound === 1))
          );
          setSecondRoundFormSteps(
            buildFormSteps(sortedMatchups.filter((m) => m.playoffRound === 2))
          );
          setThirdRoundFormSteps(
            buildFormSteps(sortedMatchups.filter((m) => m.playoffRound === 3))
          );
          setFourthRoundFormSteps(
            buildFormSteps(sortedMatchups.filter((m) => m.playoffRound === 4))
          );
        } catch (error) {
          logging.error(error, 'PicksPage');
        } finally {
          setFetchingMatchups(false);
        }
      }
    };
    fetchMatchups();
  }, [fetchingPicks, cycle.season, cycle.activeRound]);

  if (fetchingMatchups || fetchingPicks || fetchingUser) {
    return (
      <Backdrop open={fetchingMatchups || fetchingPicks || fetchingUser}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  }

  return (
    <NewPicksCard
      firstRoundPicks={firstRoundPicks}
      secondRoundPicks={secondRoundPicks}
      thirdRoundPicks={thirdRoundPicks}
      fourthRoundPicks={fourthRoundPicks}
      firstRoundSteps={firstRoundFormSteps}
      secondRoundSteps={secondRoundFormSteps}
      thirdRoundSteps={thirdRoundFormSteps}
      fourthRoundSteps={fourthRoundFormSteps}
    />
  );
};
